import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

/**
 * Factory function to create entity CRUD thunks
 * @param {Object} options - Configuration options
 * @param {string} options.entityType - The type of entity (e.g., 'variation', 'character')
 * @param {string} options.basePrefix - The base prefix for action types
 * @param {Object} options.endpoints - API endpoints for CRUD operations
 * @returns {Object} Object containing CRUD thunks
 */
export const createEntityThunks = (options) => {
  const {
    entityType,
    basePrefix,
    endpoints
  } = options;

  // Create thunk for fetching all entities
  const fetchAll = createAsyncThunk(
    `${basePrefix}/fetchAll${entityType}s`,
    async (params, { rejectWithValue }) => {
      try {
        const token = localStorage.getItem('authToken');

        // Improved URL construction
        let url;
        if (typeof endpoints.fetchAll === 'function') {
          // Evaluate the function to get the URL string
          url = endpoints.fetchAll(params);
          console.log(`Constructed URL for ${entityType}: ${url}`);

          // Validate that we got a string back
          if (typeof url !== 'string' || !url) {
            throw new Error(`Invalid URL generated for ${entityType}: ${url}`);
          }
        } else {
          // Direct string URL
          url = endpoints.fetchAll;
        }

        console.log(`Fetching ${entityType}s from URL: ${url}`);

        // Only pass params to axios.get if we didn't use a function-based endpoint
        // This prevents the "target must be an object" error when params is used to construct the URL
        const axiosConfig = {
          headers: { Authorization: `Bearer ${token}` }
        };

        // Only include params if we didn't use them to construct the URL
        if (typeof endpoints.fetchAll !== 'function') {
          axiosConfig.params = params;
        }

        const response = await axios.get(url, axiosConfig);

        return response.data;
      } catch (err) {
        console.error(`Error fetching ${entityType}s:`, err);
        return rejectWithValue(err.response?.data || `Error fetching ${entityType}s`);
      }
    }
  );

  // Create thunk for fetching a single entity
  const fetchOne = createAsyncThunk(
    `${basePrefix}/fetch${entityType}`,
    async (id, { rejectWithValue }) => {
      try {
        const token = localStorage.getItem('authToken');

        // Improved URL construction
        let url;
        if (typeof endpoints.fetchOne === 'function') {
          // Evaluate the function to get the URL string
          url = endpoints.fetchOne(id);
          console.log(`Constructed URL for fetching ${entityType}: ${url}`);

          // Validate that we got a string back
          if (typeof url !== 'string' || !url) {
            throw new Error(`Invalid URL generated for fetching ${entityType}: ${url}`);
          }
        } else {
          // Direct string URL with parameter replacement
          url = endpoints.fetchOne.replace(':id', id);
        }

        console.log(`Fetching ${entityType} from URL: ${url}`);

        // Only pass params to axios.get if we didn't use a function-based endpoint
        const axiosConfig = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const response = await axios.get(url, axiosConfig);

        return response.data;
      } catch (err) {
        console.error(`Error fetching ${entityType}:`, err);
        return rejectWithValue(err.response?.data || `Error fetching ${entityType}`);
      }
    }
  );

  // Create thunk for creating an entity
  const create = createAsyncThunk(
    `${basePrefix}/create${entityType}`,
    async (data, { rejectWithValue }) => {
      try {
        const token = localStorage.getItem('authToken');

        // Improved URL construction
        let url;
        if (typeof endpoints.create === 'function') {
          // Evaluate the function to get the URL string
          url = endpoints.create(data);
          console.log(`Constructed URL for creating ${entityType}: ${url}`);

          // Validate that we got a string back
          if (typeof url !== 'string' || !url) {
            throw new Error(`Invalid URL generated for creating ${entityType}: ${url}`);
          }
        } else {
          // Direct string URL
          url = endpoints.create;
        }

        console.log(`Creating ${entityType} at URL: ${url}`);

        const response = await axios.post(url, data, {
          headers: { Authorization: `Bearer ${token}` }
        });

        return response.data;
      } catch (err) {
        console.error(`Error creating ${entityType}:`, err);
        return rejectWithValue(err.response?.data || `Error creating ${entityType}`);
      }
    }
  );

  // Create thunk for updating an entity
  const update = createAsyncThunk(
    `${basePrefix}/update${entityType}`,
    async ({ id, data }, { rejectWithValue }) => {
      try {
        const token = localStorage.getItem('authToken');

        // Improved URL construction
        let url;
        if (typeof endpoints.update === 'function') {
          // Evaluate the function to get the URL string
          url = endpoints.update(id);
          console.log(`Constructed URL for updating ${entityType}: ${url}`);

          // Validate that we got a string back
          if (typeof url !== 'string' || !url) {
            throw new Error(`Invalid URL generated for updating ${entityType}: ${url}`);
          }
        } else {
          // Direct string URL with parameter replacement
          url = endpoints.update.replace(':id', id);
        }

        console.log(`Updating ${entityType} at URL: ${url}`);

        const response = await axios.put(url, data, {
          headers: { Authorization: `Bearer ${token}` }
        });

        return response.data;
      } catch (err) {
        console.error(`Error updating ${entityType}:`, err);
        return rejectWithValue(err.response?.data || `Error updating ${entityType}`);
      }
    }
  );

  // Create thunk for deleting an entity
  const remove = createAsyncThunk(
    `${basePrefix}/delete${entityType}`,
    async (id, { rejectWithValue }) => {
      try {
        const token = localStorage.getItem('authToken');

        // Improved URL construction
        let url;
        if (typeof endpoints.delete === 'function') {
          // Evaluate the function to get the URL string
          url = endpoints.delete(id);
          console.log(`Constructed URL for deleting ${entityType}: ${url}`);

          // Validate that we got a string back
          if (typeof url !== 'string' || !url) {
            throw new Error(`Invalid URL generated for deleting ${entityType}: ${url}`);
          }
        } else {
          // Direct string URL with parameter replacement
          url = endpoints.delete.replace(':id', id);
        }

        console.log(`Deleting ${entityType} at URL: ${url}`);

        await axios.delete(url, {
          headers: { Authorization: `Bearer ${token}` }
        });

        return { id };
      } catch (err) {
        console.error(`Error deleting ${entityType}:`, err);
        return rejectWithValue(err.response?.data || `Error deleting ${entityType}`);
      }
    }
  );

  return {
    fetchAll,
    fetchOne,
    create,
    update,
    remove
  };
};

/**
 * Create a thunk for fetching a signed URL
 * @param {Object} options - Configuration options
 * @param {string} options.basePrefix - The base prefix for action types
 * @param {string} options.endpoint - API endpoint for fetching signed URL
 * @returns {Function} Thunk for fetching signed URL
 */
export const createSignedUrlThunk = (options) => {
  const { basePrefix, endpoint } = options;

  return createAsyncThunk(
    `${basePrefix}/getSignedUrl`,
    async ({ url, userId }, { rejectWithValue }) => {
      try {
        // Improved URL construction
        let endpointUrl;
        if (typeof endpoint === 'function') {
          // Evaluate the function to get the URL string
          endpointUrl = endpoint({ url, userId });
          console.log(`Constructed URL for signed URL request: ${endpointUrl}`);

          // Validate that we got a string back
          if (typeof endpointUrl !== 'string' || !endpointUrl) {
            throw new Error(`Invalid URL generated for signed URL request: ${endpointUrl}`);
          }
        } else {
          // Direct string URL
          endpointUrl = endpoint;
        }

        console.log(`Requesting signed URL from: ${endpointUrl}`);

        const response = await axios.post(endpointUrl, {
          url,
          userId
        });

        return {
          originalUrl: url,
          signedUrl: response.data.signedUrl,
          userId
        };
      } catch (err) {
        console.error('Error getting signed URL:', err);
        return rejectWithValue(err.response?.data || 'Error getting signed URL');
      }
    }
  );
};

/**
 * Create a thunk for uploading a file
 * @param {Object} options - Configuration options
 * @param {string} options.basePrefix - The base prefix for action types
 * @param {string} options.endpoint - API endpoint for uploading file
 * @returns {Function} Thunk for uploading file
 */
export const createFileUploadThunk = (options) => {
  const { basePrefix, endpoint } = options;

  return createAsyncThunk(
    `${basePrefix}/uploadFile`,
    async ({ file, metadata }, { rejectWithValue }) => {
      try {
        const token = localStorage.getItem('authToken');
        const formData = new FormData();

        formData.append("file", file);

        // Add metadata to form data
        Object.entries(metadata).forEach(([key, value]) => {
          formData.append(key, value);
        });

        // Improved URL construction
        let url;
        if (typeof endpoint === 'function') {
          // Evaluate the function to get the URL string
          url = endpoint(metadata);
          console.log(`Constructed URL for file upload: ${url}`);

          // Validate that we got a string back
          if (typeof url !== 'string' || !url) {
            throw new Error(`Invalid URL generated for file upload: ${url}`);
          }
        } else {
          // Direct string URL
          url = endpoint;
        }

        console.log(`Uploading file to URL: ${url}`);

        const response = await axios.post(url, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percentCompleted}%`);
          }
        });

        return response.data;
      } catch (err) {
        console.error('Error uploading file:', err);
        return rejectWithValue(err.response?.data || 'Error uploading file');
      }
    }
  );
};

/**
 * Create a thunk for ensuring selections are loaded
 * @param {Object} options - Configuration options
 * @param {string} options.basePrefix - The base prefix for action types
 * @param {Function} options.getSelectionId - Function to get selection ID from state
 * @param {Function} options.getSelectedEntity - Function to get selected entity from state
 * @param {Function} options.fetchEntity - Thunk to fetch entity
 * @param {Function} options.clearSelection - Action creator to clear selection
 * @returns {Function} Thunk for ensuring selections are loaded
 */
export const createEnsureSelectionsLoadedThunk = (options) => {
  const {
    basePrefix,
    getSelectionId,
    getSelectedEntity,
    fetchEntity,
    clearSelection
  } = options;

  return createAsyncThunk(
    `${basePrefix}/ensureSelectionsLoaded`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      const selectionId = getSelectionId(state);
      const selectedEntity = getSelectedEntity(state);

      if (selectionId && !selectedEntity) {
        try {
          await dispatch(fetchEntity(selectionId)).unwrap();
        } catch (error) {
          console.error(`Error loading selection: ${error.message}`);
          dispatch(clearSelection());
        }
      }

      return true;
    }
  );
};
